import { AgBoxPlotSeriesOptions } from 'ag-charts-community'

import { CustomAgChart } from '@fortem-monorepo/react-ag'
import type { ICReport } from '@fortem-monorepo/web-sdk'

export interface CtaExposureBoxPlotChartProps {
  weights: ICReport.CtaExposureData['weights']
}

export const CtaExposureBoxPlotChart: React.FC<CtaExposureBoxPlotChartProps> = ({ weights }) => {
  const data = transformWeightsToBoxPlotData(weights)
  const boxPlotSeries: AgBoxPlotSeriesOptions = {
    type: 'box-plot',
    direction: 'horizontal',
    data,

    xKey: 'role',
    xName: 'Role',

    yName: 'Weight',

    minKey: 'min',

    q1Key: 'q1',
    q1Name: '25th Percentile',

    medianKey: 'median',
    medianName: 'Median',

    q3Key: 'q3',
    q3Name: '75th Percentile',

    maxKey: 'max',

    whisker: {
      stroke: '#333333',
      strokeWidth: 1,
    },

    itemStyler: () => {
      return {
        strokeWidth: 1,
        fillOpacity: 0,
        stroke: '#333',
      }
    },
  }

  const outliersData = data.flatMap((item) => {
    return item.outliers.map((value) => ({
      role: item.role,
      weight: value,
    }))
  })

  return (
    <CustomAgChart
      style={{ height: '100%' }}
      options={{
        axes: [
          {
            type: 'number',
            position: 'bottom',
            crossLines: [
              {
                type: 'line',
                value: 0,
                stroke: '#257BB6',
                strokeWidth: 1,
                strokeOpacity: 1,
                lineDash: [4, 2],
                label: {
                  enabled: false,
                },
              },
            ],
          },
          {
            type: 'category',
            position: 'left',
          },
        ],
        series: [
          // ... Primary Box Plot
          boxPlotSeries,
          // ... Outlier Scatter
          {
            type: 'scatter',
            data: outliersData,
            yKey: 'role',
            xKey: 'weight',
            xName: 'Outlier',

            size: 4,
            fillOpacity: 0,
            stroke: 'black',
          },
          // ... Current Weight Scatter
          {
            type: 'scatter',
            data: data,

            xKey: 'currentWeight',
            yKey: 'role',

            xName: 'Current weight',
            yName: 'Role',

            size: 7,
            shape: 'diamond',
            strokeWidth: 1,

            stroke: '#2077B4',
            fill: '#2077B4',
          },
        ],
      }}
    />
  )
}

const quantile = (sortedValues: number[], percentile: number): number => {
  if (sortedValues.length === 0) {
    throw new Error('Cannot calculate a quantile from an empty array')
  }

  const position = (sortedValues.length - 1) * percentile
  const lowerIndex = Math.floor(position)
  const upperIndex = Math.ceil(position)

  if (lowerIndex === upperIndex) {
    return sortedValues[lowerIndex]
  }

  const fraction = position - lowerIndex

  return sortedValues[lowerIndex] + (sortedValues[upperIndex] - sortedValues[lowerIndex]) * fraction
}

type WeightBoxPlotDatum = {
  role: string
  min: number
  q1: number
  median: number
  q3: number
  max: number
  outliers: number[]
  currentWeight: number
}
const transformWeightsToBoxPlotData = (
  weights: ICReport.CtaExposureData['weights'],
): WeightBoxPlotDatum[] => {
  const weightsByRole = new Map<string, ICReport.CtaExposureData['weights']>()

  for (const item of weights) {
    if (!Number.isFinite(item.weight)) continue

    const role = `${item.asset_class} - ${item.instrument_name}`
    const values = weightsByRole.get(role) ?? []

    values.push(item)
    weightsByRole.set(role, values)
  }

  return Array.from(weightsByRole, ([role, items]) => {
    const sortedValues = items.map((item) => item.weight).toSorted((a, b) => a - b)

    const latestItem = items.toSorted((a, b) => {
      const reviewDateDifference =
        new Date(b.review_date).getTime() - new Date(a.review_date).getTime()

      if (reviewDateDifference !== 0) {
        return reviewDateDifference
      }

      const fileDateDifference =
        new Date(b.file_datetime_utc).getTime() - new Date(a.file_datetime_utc).getTime()

      if (fileDateDifference !== 0) {
        return fileDateDifference
      }

      return (
        new Date(b.insertion_datetime_utc).getTime() - new Date(a.insertion_datetime_utc).getTime()
      )
    })[0]

    const q1 = quantile(sortedValues, 0.25)
    const median = quantile(sortedValues, 0.5)
    const q3 = quantile(sortedValues, 0.75)

    const iqr = q3 - q1
    const lowerFence = q1 - 1.5 * iqr
    const upperFence = q3 + 1.5 * iqr

    const nonOutliers = sortedValues.filter((value) => value >= lowerFence && value <= upperFence)

    const outliers = sortedValues.filter((value) => value < lowerFence || value > upperFence)

    return {
      role,
      min: nonOutliers[0] ?? sortedValues[0],
      q1,
      median,
      q3,
      max: nonOutliers.at(-1) ?? sortedValues.at(-1)!,
      outliers,
      currentWeight: latestItem.weight,
    }
  })
}
